import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Plus, TrendingUp, X } from "react-feather";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { programService, type ProgramTier } from "@/services/program.service";
import { Button } from "@/components/ui/button";

const MAX_TIERS = 6;

type Draft = { name: string; threshold: string; commissionValue: string; perks: string };

interface ProgramTiersCardProps {
  programId: string;
  baseCommissionValue: number;
  commissionType: "PERCENTAGE" | "FIXED";
}

function toDraft(tier: ProgramTier): Draft {
  return {
    name: tier.name,
    threshold: String(tier.threshold),
    commissionValue: String(tier.commissionValue),
    perks: tier.perks ?? "",
  };
}

/**
 * Brand tiers: commission steps an affiliate climbs by all-time sales with
 * this program. The program's rate is the base; each tier lifts it once its
 * sales threshold is reached. Saved as a whole ladder.
 */
export function ProgramTiersCard({
  programId,
  baseCommissionValue,
  commissionType,
}: ProgramTiersCardProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [drafts, setDrafts] = useState<Draft[]>([]);

  const { data: tiers, isLoading } = useQuery({
    queryKey: ["program-tiers", programId],
    queryFn: () => programService.getProgramTiers(programId),
  });

  useEffect(() => {
    if (tiers) setDrafts(tiers.map(toDraft));
  }, [tiers]);

  const saved = useMemo(() => (tiers ?? []).map(toDraft), [tiers]);
  const isDirty = JSON.stringify(drafts) !== JSON.stringify(saved);

  const { mutate: save, isPending } = useMutation({
    mutationFn: () =>
      programService.updateProgramTiers(
        programId,
        drafts.map((d) => ({
          name: d.name.trim(),
          threshold: Number(d.threshold),
          commissionValue: Number(d.commissionValue),
          perks: d.perks.trim() || undefined,
        }))
      ),
    onSuccess: (next) => {
      queryClient.setQueryData(["program-tiers", programId], next);
      toast.success(next.length ? "Tiers saved" : "Tiers removed");
    },
    onError: (err: unknown) => {
      const message =
        (err as AxiosError<{ message: string | string[] }>)?.response?.data?.message;
      toast.error(Array.isArray(message) ? message[0] : message || "Failed to save tiers");
    },
  });

  const unit = commissionType === "FIXED" ? "$" : "%";

  const validation = useMemo(() => {
    let lastThreshold = 0;
    for (const d of drafts) {
      if (!d.name.trim()) return "Every tier needs a name.";
      const threshold = Number(d.threshold);
      if (!(threshold > lastThreshold)) return "Thresholds must go up from one tier to the next.";
      lastThreshold = threshold;
      if (!(Number(d.commissionValue) >= 0)) return "Commission must be a number.";
    }
    return null;
  }, [drafts]);

  const update = (index: number, patch: Partial<Draft>) =>
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  const addTier = () => {
    if (drafts.length >= MAX_TIERS) return;
    const last = drafts[drafts.length - 1];
    setDrafts((prev) => [
      ...prev,
      {
        name: "",
        threshold: last ? String(Number(last.threshold) * 2 || 1000) : "1000",
        commissionValue: last ? last.commissionValue : String(baseCommissionValue),
        perks: "",
      },
    ]);
  };

  const summary = isLoading
    ? "Loading…"
    : saved.length
      ? `${saved.length} tier${saved.length === 1 ? "" : "s"} above the ${baseCommissionValue}${unit} base`
      : "One flat rate — add tiers to reward volume";

  return (
    <section id="PT-tiers-card" className="rounded-xl border border-border bg-card shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left"
        aria-expanded={isOpen}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
          <TrendingUp className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-foreground">Commission tiers</span>
          <span className="block text-xs text-muted-foreground">{summary}</span>
        </span>
        <ChevronDown
          className={`w-5 h-5 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="border-t border-border px-5 py-4 space-y-4">
          <p className="text-xs text-muted-foreground max-w-prose">
            Affiliates start on your base rate. When their all-time sales with this program
            reach a tier's threshold, every later order earns that tier's rate. Affiliates see
            the ladder and their progress on their dashboard.
          </p>

          <div className="space-y-2">
            {/* Header row */}
            <div className="hidden sm:grid grid-cols-[1fr_120px_100px_1.4fr_32px] gap-2 px-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <span>Tier</span>
              <span>Sales to reach</span>
              <span>Rate</span>
              <span>Perks (optional)</span>
              <span />
            </div>

            {/* Base rung, read-only */}
            <div className="grid grid-cols-2 sm:grid-cols-[1fr_120px_100px_1.4fr_32px] gap-2 rounded-lg bg-muted/60 px-3 py-2 text-sm">
              <span className="font-medium text-foreground">Start</span>
              <span className="text-muted-foreground">Joined</span>
              <span className="font-mono text-muted-foreground">
                {baseCommissionValue}
                {unit}
              </span>
              <span className="text-muted-foreground hidden sm:block">Program rate</span>
              <span />
            </div>

            {drafts.map((draft, index) => (
              <div
                key={index}
                className="grid grid-cols-2 sm:grid-cols-[1fr_120px_100px_1.4fr_32px] gap-2 items-center"
              >
                <input
                  type="text"
                  value={draft.name}
                  onChange={(e) => update(index, { name: e.target.value })}
                  placeholder={["Silver", "Gold", "Platinum"][index] ?? "Tier name"}
                  maxLength={40}
                  aria-label="Tier name"
                  className="w-full rounded-lg border border-border bg-transparent px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                />
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    $
                  </span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={draft.threshold}
                    onChange={(e) => update(index, { threshold: e.target.value })}
                    aria-label="Sales needed to reach this tier"
                    className="w-full rounded-lg border border-border bg-transparent pl-6 pr-2 py-2 text-sm tabular-nums focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                  />
                </div>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={draft.commissionValue}
                    onChange={(e) => update(index, { commissionValue: e.target.value })}
                    aria-label="Commission at this tier"
                    className="w-full rounded-lg border border-border bg-transparent px-3 pr-7 py-2 text-sm tabular-nums focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    {unit}
                  </span>
                </div>
                <input
                  type="text"
                  value={draft.perks}
                  onChange={(e) => update(index, { perks: e.target.value })}
                  placeholder="e.g. early access, gifting"
                  maxLength={160}
                  aria-label="Perks"
                  className="w-full rounded-lg border border-border bg-transparent px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setDrafts((prev) => prev.filter((_, i) => i !== index))}
                  aria-label={`Remove ${draft.name || "tier"}`}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addTier}
              disabled={drafts.length >= MAX_TIERS}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add tier
              {drafts.length >= MAX_TIERS ? ` (max ${MAX_TIERS})` : ""}
            </Button>

            {isDirty && (
              <div className="flex items-center gap-3">
                {validation && <span className="text-xs text-red-600">{validation}</span>}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDrafts(saved)}
                  disabled={isPending}
                >
                  Discard
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => save()}
                  disabled={isPending || !!validation}
                >
                  {isPending ? "Saving…" : "Save tiers"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
